<app-navbar />

<main id="main-content">
  <section class="page-hero">
    <div class="container">
      <app-breadcrumb [trail]="[{ label: 'About' }]" />
      <span class="eyebrow">About us</span>
      <h1>America's source of truth for deceased verification</h1>
      <p>
        Every industry is affected by death, yet there has never been a reliable, centralized and
        accessible way to confirm that someone has passed away. VerifyDeceased was created to close
        that gap.
      </p>
      <p class="mission-line">{{ site.mission }}</p>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Our story</span>
        <h2 class="section-title">We did not set out to build this</h2>
        <p class="section-lead">
          VerifyDeceased came out of {{ stats.obituariesResearched }} obituaries and one problem we
          could not stop running into. Here is how we got here.
        </p>
      </div>

      <ol class="story-timeline">
        @for (chapter of story; track chapter.year) {
          <li class="story-step">
            <div class="story-marker">
              <span class="story-year">{{ chapter.year }}</span>
            </div>
            <div class="story-body">
              <h3>{{ chapter.title }}</h3>
              <p>{{ chapter.text }}</p>
              @if (chapter.highlight) {
                <p class="story-highlight">{{ chapter.highlight }}</p>
              }
            </div>
          </li>
        }
      </ol>

      <div class="story-stats">
        <div class="story-stat">
          <span class="stat-value">{{ stats.obituariesResearched }}</span>
          <span class="stat-label">Obituaries researched since {{ site.researchSince }}</span>
        </div>
        <div class="story-stat">
          <span class="stat-value">{{ stats.weeklyRecords }}</span>
          <span class="stat-label">New confirmed records each week</span>
        </div>
        <div class="story-stat">
          <span class="stat-value">{{ stats.dataFreshnessDays }} days</span>
          <span class="stat-label">Typical time to reflect a passing</span>
        </div>
        <div class="story-stat">
          <span class="stat-value">{{ stats.statesCovered }} states</span>
          <span class="stat-label">Nationwide coverage plus DC</span>
        </div>
      </div>
    </div>
  </section>

  <section class="section section-tint">
    <div class="container">
      <div class="founder-panel">
        <figure class="founder-portrait">
          <img [src]="founder.photo" [alt]="founder.photoAlt" width="480" height="560" loading="lazy" />
          <figcaption>
            <span class="founder-portrait-name">{{ founder.name || founder.role }}</span>
            @if (founder.name) {
              <span class="founder-portrait-role">{{ founder.role }}</span>
            }
          </figcaption>
        </figure>
        <div class="founder-message">
          <span class="eyebrow">From the founder</span>
          <h2 class="section-title">Why we built this</h2>
          @for (paragraph of founder.paragraphs; track paragraph) {
            <p>{{ paragraph }}</p>
          }
          <div class="founder-signature">
            <span class="founder-signature-name">{{ founder.name || founder.role }}</span>
            @if (founder.name) {
              <span class="founder-signature-role">{{ founder.role }}, {{ site.name }}</span>
            } @else {
              <span class="founder-signature-role">{{ site.name }}</span>
            }
            @if (founder.linkedIn) {
              <a class="founder-signature-link" [href]="founder.linkedIn" rel="noopener" target="_blank">
                Connect on LinkedIn
              </a>
            }
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="section section-alt">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">The data</span>
        <h2 class="section-title">One of the most comprehensive deceased individual databases in America</h2>
        <p class="section-lead">
          Our data set covers people who have passed away whether or not they owned real estate,
          which is the point where most other sources stop.
        </p>
      </div>
      <div class="grid-3">
        @for (item of datasetItems; track item.title) {
          <div class="feature-card">
            <span class="icon-badge" aria-hidden="true" [innerHTML]="iconFor(item.icon)"></span>
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
          </div>
        }
      </div>
      <div class="outcome-panel">
        <h3>What organizations do with it</h3>
        <ul class="check-list">
          @for (outcome of outcomes; track outcome) {
            <li>{{ outcome }}</li>
          }
        </ul>
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Why this matters</span>
        <h2 class="section-title">The blind window after a passing</h2>
        <p class="section-lead">
          Death related identity theft is one of the fastest growing financial crimes in the United
          States. In the first three months after someone passes away there is often no publicly
          available confirmation of their death, which leaves families vulnerable and institutions
          exposed.
        </p>
      </div>
      <div class="grid-4">
        @for (type of fraudTypes; track type.title) {
          <div class="fraud-card">
            <span class="fraud-icon" aria-hidden="true" [innerHTML]="iconFor(type.icon)"></span>
            <h3>{{ type.title }}</h3>
            <p>{{ type.description }}</p>
          </div>
        }
      </div>
      <p class="section-lead text-center mt-32 narrow">
        This causes real financial loss for businesses and real emotional harm for families. Death
        Verifier provides the confirmation needed to stop that fraud before it starts.
      </p>
    </div>
  </section>

  <section class="section section-alt">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Our commitment</span>
        <h2 class="section-title">Accuracy, transparency and innovation</h2>
        <p class="section-lead">
          Our goal is to give industries verification tools they can trust, protect families, and
          bring clarity to a problem that touches every sector of the economy.
        </p>
      </div>
      <div class="grid-3">
        @for (value of values; track value.title) {
          <div class="feature-card">
            <h3>{{ value.title }}</h3>
            <p>{{ value.description }}</p>
          </div>
        }
      </div>
    </div>
  </section>

  @if (leadership.length) {
    <section class="section section-tint">
      <div class="container">
        <div class="section-head">
          <span class="eyebrow">Leadership</span>
          <h2 class="section-title">The people accountable for this platform</h2>
          <p class="section-lead">
            You should know who you are trusting with your customer file. These are the people who
            own the data, the security program and the commitments we make to you.
          </p>
        </div>
        <div class="team-grid">
          @for (member of leadership; track member.name) {
            <div class="team-card">
              @if (member.photo) {
                <img class="team-photo" [src]="member.photo" [alt]="member.name" width="96" height="96" loading="lazy" />
              } @else {
                <span class="team-initials" aria-hidden="true">{{ initials(member.name) }}</span>
              }
              <h3>{{ member.name }}</h3>
              <span class="team-role">{{ member.role }}</span>
              <p>{{ member.bio }}</p>
              <span class="team-focus">{{ member.focus }}</span>
              @if (member.linkedIn) {
                <a class="team-link" [href]="member.linkedIn" rel="noopener" target="_blank">
                  Connect on LinkedIn
                </a>
              }
            </div>
          }
        </div>
      </div>
    </section>
  }

  <section class="section">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Accountability</span>
        <h2 class="section-title">Who you are actually dealing with</h2>
        <p class="section-lead">
          Buying data is a trust decision. Here is everything a procurement or third party risk team
          normally has to dig for, in one place.
        </p>
      </div>
      <div class="fact-grid">
        @for (fact of companyFacts; track fact.label) {
          <div class="fact-card">
            <span class="fact-label">{{ fact.label }}</span>
            <span class="fact-value">{{ fact.value }}</span>
            <p>{{ fact.detail }}</p>
          </div>
        }
      </div>
      <div class="contact-strip">
        <div class="contact-strip-item">
          <span class="contact-strip-label">Call us</span>
          <a [href]="site.phoneHref">{{ site.phone }}</a>
        </div>
        <div class="contact-strip-item">
          <span class="contact-strip-label">Sales</span>
          <a [href]="'mailto:' + site.email">{{ site.email }}</a>
        </div>
        <div class="contact-strip-item">
          <span class="contact-strip-label">Support</span>
          <a [href]="'mailto:' + site.supportEmail">{{ site.supportEmail }}</a>
        </div>
        <div class="contact-strip-item">
          <span class="contact-strip-label">Registered address</span>
          <span>{{ site.address }}</span>
        </div>
      </div>
      <div class="text-center mt-32">
        <a class="btn btn-outline" [routerLink]="[routes.security.url]">Read our security and compliance detail</a>
      </div>
    </div>
  </section>

  <section class="section section-alt">
    <div class="container">
      <div class="data-notice">
        <span class="eyebrow">Data notice</span>
        <h2 class="section-title">How our data should be used</h2>
        <p>
          As with any large scale data set, VerifyDeceased does not claim responsibility for the
          accuracy or completeness of the information provided. Users must independently verify all
          data and carry out their own research to confirm the validity of any record.
        </p>
        <p>
          All information is offered as is and should be used as a supplemental resource rather than
          a sole source of truth. That is why every match we return carries a confidence score, and
          why we recommend a documented review step before any account is frozen or closed.
        </p>
        <p class="data-notice-links">
          <a [routerLink]="[routes.terms.url]">Terms of service</a>
          <a [routerLink]="[routes.privacy.url]">Privacy policy</a>
          <a [routerLink]="[routes.security.url]">Security and compliance</a>
        </p>
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="cta-band">
        <h2>Want to see the data in action?</h2>
        <p>Run a sample verification or talk to our team about your use case.</p>
        <div class="actions-row center">
          <a class="btn btn-primary btn-lg" [routerLink]="[routes.accountRegister.url]">Get started</a>
          <a class="btn btn-outline btn-lg" [routerLink]="[routes.contact.url]">Contact us</a>
        </div>
      </div>
    </div>
  </section>
</main>

<app-footer />
